// UX133 (docs/build-briefs/UX133-time-zone-offset-labels.md): the one place a time zone is labelled and ordered for a
// picker. Every time zone choice in the product (software owner console, location settings, per-location settings, the
// owner setup wizard) builds its options here, so a person always reads the UTC offset and a plain zone name, never a
// bare city. The stored value is always the IANA id; only the label is built here.

/** The IANA zones of the United States. A franchise network is US-first; these list first in every picker. */
export const US_TIME_ZONES: ReadonlySet<string> = new Set([
  "America/Adak",
  "America/Anchorage",
  "America/Boise",
  "America/Chicago",
  "America/Denver",
  "America/Detroit",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Juneau",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Los_Angeles",
  "America/Menominee",
  "America/Metlakatla",
  "America/New_York",
  "America/Nome",
  "America/North_Dakota/Beulah",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/Phoenix",
  "America/Sitka",
  "America/Yakutat",
  "Pacific/Honolulu",
]);

/** The offset from UTC, in minutes, that `zone` observes at `at` (DST aware). Computed with Intl: the zone's wall
 * clock read back as if it were UTC, minus the real instant. */
export function zoneOffsetMinutes(zone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(at);
  const read = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const wallAsUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour") % 24,
    read("minute"),
    read("second"),
  );
  return Math.round((wallAsUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/** "UTC-05:00", "UTC+05:30", "UTC+00:00". An ordinary hyphen-minus, never a dash character. */
export function formatUtcOffset(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

/** The city a person reads: "America/Indiana/Indianapolis" is "Indianapolis". */
export function zoneCity(zone: string): string {
  return zone.split("/").slice(-1)[0]?.replace(/_/g, " ") ?? zone;
}

// The United States zones by their standard (winter) offset, so the name does not depend on what a given browser's
// ICU data calls Phoenix.
const US_NAME_BY_STANDARD_OFFSET: ReadonlyMap<number, string> = new Map([
  [-300, "Eastern Time"],
  [-360, "Central Time"],
  [-420, "Mountain Time"],
  [-480, "Pacific Time"],
  [-540, "Alaska Time"],
  [-600, "Hawaii-Aleutian Time"],
]);

function intlGenericName(zone: string, at: Date): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longGeneric" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    const value = part?.value.trim() ?? "";
    // An offset-only answer ("GMT+00:00") names nothing; the offset is already in the label.
    return /^GMT[+-]/.test(value) ? "" : value;
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    return "";
  }
}

/** True when the zone observes the same offset in January and July of `at`'s year (it never changes for DST). */
function hasNoDst(zone: string, at: Date): boolean {
  const year = at.getUTCFullYear();
  return (
    zoneOffsetMinutes(zone, new Date(Date.UTC(year, 0, 1, 12))) ===
    zoneOffsetMinutes(zone, new Date(Date.UTC(year, 6, 1, 12)))
  );
}

/** "Eastern Time", "Mountain Time, no DST", "India Standard Time", or "" when nothing readable exists. */
export function zoneReadableName(zone: string, at: Date): string {
  if (zone === "UTC") return "UTC";
  if (US_TIME_ZONES.has(zone)) {
    const year = at.getUTCFullYear();
    const winter = Math.min(
      zoneOffsetMinutes(zone, new Date(Date.UTC(year, 0, 1, 12))),
      zoneOffsetMinutes(zone, new Date(Date.UTC(year, 6, 1, 12))),
    );
    const name = US_NAME_BY_STANDARD_OFFSET.get(winter) ?? intlGenericName(zone, at);
    return name !== "" && hasNoDst(zone, at) ? `${name}, no DST` : name;
  }
  return intlGenericName(zone, at);
}

/** "UTC-05:00 Eastern Time (New York)", "UTC-07:00 Mountain Time, no DST (Phoenix)", "UTC+00:00 UTC". */
export function zoneOptionLabel(zone: string, at: Date = new Date()): string {
  const offset = formatUtcOffset(zoneOffsetMinutes(zone, at));
  if (zone === "UTC") return `${offset} UTC`;
  const name = zoneReadableName(zone, at);
  const city = zoneCity(zone);
  return name === "" ? `${offset} (${city})` : `${offset} ${name} (${city})`;
}

/** FRANWELCOME1: the plain name a person says, "Eastern Time", with no offset and no city. A zone with no readable name
 * (rare) falls back to its city, so the line is never empty. */
export function zonePlainName(zone: string, at: Date = new Date()): string {
  const name = zoneReadableName(zone, at);
  return name === "" ? zoneCity(zone) : name;
}

export interface ZoneOption {
  /** The stored IANA id. */
  value: string;
  label: string;
  offsetMinutes: number;
}

export interface ZoneOptionGroup {
  label: string;
  options: ZoneOption[];
}

const REGION_ORDER: readonly string[] = [
  "Africa",
  "Americas",
  "Antarctica",
  "Arctic",
  "Asia",
  "Atlantic",
  "Australia",
  "Europe",
  "Indian",
  "Pacific",
];

/** The picker group a zone belongs to. */
export function zoneGroupLabel(zone: string): string {
  if (US_TIME_ZONES.has(zone)) return "United States";
  if (zone === "UTC") return "UTC";
  const area = zone.split("/")[0] ?? zone;
  if (area === "America") return "Americas";
  return REGION_ORDER.includes(area) ? area : "Other";
}

/** The picker's groups: United States first, then regions A to Z, then UTC. Inside a group the zones run by offset
 * (west to east), then city. `current` (a stored zone the list lacks) is kept so it is never lost from view. */
export function buildZoneGroups(
  zones: readonly string[],
  options: { at?: Date; current?: string } = {},
): ZoneOptionGroup[] {
  const at = options.at ?? new Date();
  const all = new Set(zones);
  if (options.current !== undefined && options.current !== "") all.add(options.current);
  const byGroup = new Map<string, ZoneOption[]>();
  for (const zone of all) {
    let option: ZoneOption;
    try {
      option = { value: zone, label: zoneOptionLabel(zone, at), offsetMinutes: zoneOffsetMinutes(zone, at) };
    } catch (error) {
      // A stored value Intl does not know stays pickable under its own id; only a RangeError means "unknown zone".
      if (!(error instanceof RangeError)) throw error;
      option = { value: zone, label: zone, offsetMinutes: 0 };
    }
    const group = zoneGroupLabel(zone);
    byGroup.set(group, [...(byGroup.get(group) ?? []), option]);
  }
  const order = ["United States", ...REGION_ORDER, "Other", "UTC"];
  return order
    .filter((label) => byGroup.has(label))
    .map((label) => ({
      label,
      options: (byGroup.get(label) ?? []).sort(
        (a, b) =>
          a.offsetMinutes - b.offsetMinutes ||
          zoneCity(a.value).localeCompare(zoneCity(b.value)) ||
          a.value.localeCompare(b.value),
      ),
    }));
}

/** What a screen reader hears for a time zone option: the label and the stored identifier. */
export function zoneAccessibleName(zone: string, at: Date = new Date()): string {
  return zone === "UTC" ? "Time zone, UTC+00:00 UTC" : `Time zone, ${zoneOptionLabel(zone, at)}, ${zone}`;
}

/** Fills `select` with grouped options. The stored IANA id is each option's value. */
export function fillZoneOptions(
  select: HTMLSelectElement,
  zones: readonly string[],
  selected: string,
  at: Date = new Date(),
): void {
  const groups = buildZoneGroups(zones, { at, current: selected });
  select.replaceChildren(
    ...groups.map((group) => {
      const node = document.createElement("optgroup");
      node.label = group.label;
      node.append(
        ...group.options.map((zone) => {
          const option = Object.assign(new Option(zone.label, zone.value), {
            selected: zone.value === selected,
          });
          option.setAttribute("aria-label", zoneAccessibleName(zone.value, at));
          return option;
        }),
      );
      return node;
    }),
  );
  select.value = selected;
  select.setAttribute("aria-label", zoneAccessibleName(selected, at));
  if (select.dataset["zoneNameBound"] !== "1") {
    select.dataset["zoneNameBound"] = "1";
    select.addEventListener("change", () =>
      select.setAttribute("aria-label", zoneAccessibleName(select.value, at)),
    );
  }
}
